import { ConnectionStatus, Network } from "@capacitor/network";

type Listener = (online: boolean) => void;
type SetupFn = (setOnline: Listener) => (() => void) | undefined;

class Subscribable<TListener extends Function> {
  protected listeners = new Set<TListener>();

  constructor() {
    this.subscribe = this.subscribe.bind(this);
  }

  subscribe(listener: TListener) {
    this.listeners.add(listener);

    this.onSubscribe();

    return () => {
      this.listeners.delete(listener);
      this.onUnsubscribe();
    };
  }

  hasListeners() {
    return this.listeners.size > 0;
  }

  protected onSubscribe() {}

  protected onUnsubscribe() {}
}

class NetworkController extends Subscribable<Listener> {
  private online = true;
  private cleanup?: () => void;

  private setup: SetupFn;

  constructor() {
    super();
    this.setup = (onOnline) => {
      const handleNetworkChange = (status: ConnectionStatus) => {
        onOnline(status.connected);
      };

      Network.addListener("networkStatusChange", handleNetworkChange);

      return () => {
        Network.removeAllListeners();
      };
    };

    this.setEventListener(this.setup);
  }

  protected onSubscribe() {
    if (!this.cleanup) {
      this.setEventListener(this.setup);
    }
  }

  protected onUnsubscribe() {
    if (!this.hasListeners() && this.cleanup) {
      this.cleanup();
      this.cleanup = undefined;
    }
  }

  private setEventListener(setup: SetupFn) {
    if (this.cleanup) {
      this.cleanup();
    }

    this.cleanup = setup(this.setOnline.bind(this));
  }

  private setOnline(online: boolean) {
    if (this.online !== online) {
      this.online = online;
      // Notify all listeners about the change
      this.listeners.forEach((listener) => listener(online));
    }
  }

  isOnline() {
    return this.online;
  }
}

export const networkController = new NetworkController();
